import { Link } from 'react-router-dom'
import { Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface UnauthorizedModuleProps {
  moduleName: string
  /**
   * - `page`: full-page denial (default — same as the original PR #174 behavior)
   * - `inline`: compact card for use inside a drawer / sub-panel
   */
  mode?: 'page' | 'inline'
  /**
   * Optional override for the help text. Use it to surface a specific permission
   * the user is missing, or to explain a customer ownership change.
   */
  helperText?: string
  /**
   * Page mode only: show the "Back to Dashboard" button (default). Turn it off when the lock
   * sits under other content the user CAN use, so it does not read as "leave this page".
   */
  showBackLink?: boolean
}

/**
 * Friendly denial UI when a user lacks the required module / permission.
 *
 * Per RBAC Module spec #10 §2.4 + extension in §3.6 (inline mode for drawers).
 */
export function UnauthorizedModule({
  moduleName,
  mode = 'page',
  helperText,
  showBackLink = true
}: UnauthorizedModuleProps) {
  const message =
    helperText ??
    `You don't have permission to access the ${moduleName} module. Contact your administrator if you need access.`

  if (mode === 'inline') {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed bg-muted/40 px-6 py-10 text-center">
        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-muted">
          <Lock className="h-5 w-5 text-muted-foreground" />
        </div>
        <h3 className="text-sm font-semibold">{moduleName} access required</h3>
        <p className="mt-1 max-w-xs text-xs text-muted-foreground">{message}</p>
      </div>
    )
  }

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
        <Lock className="h-8 w-8 text-muted-foreground" />
      </div>
      <h2 className="text-2xl font-semibold">{moduleName} access required</h2>
      <p className="mt-2 max-w-md text-muted-foreground">{message}</p>
      {showBackLink && (
        <Button asChild className="mt-6">
          <Link to="/">Back to Dashboard</Link>
        </Button>
      )}
    </div>
  )
}
